<template>
  <div class="key-points-wrapper">
    <FloatingCard v-if="!loading">
      <BotMessageHeader :title="$t('reviewing.key_points.tab_title')">
        <template #actions>
          <IconButton
            icon-name="copy-text"
            :tooltip-message="$t('legal_assistant.actions.copy')"
            @click="onCopyKeypoints"
          />
          <IconButton
            icon-name="insert-text"
            :tooltip-message="$t('legal_assistant.actions.insert')"
            @click="onInsertKeypoints"
          />
        </template>
      </BotMessageHeader>

      <div class="key-points-column" v-if="keyPoints.length">
        <!-- TODO: Comment menawhile by request -->
        <!-- <p v-if="documentName" class="doc-name">{{ documentName }}</p> -->
        <div
          v-for="(keyPoint, index) in keyPoints"
          :class="[
            'key-point',
            {
              active: referenceStore.selectedReference?.renderId === keyPoint.id
            }
          ]"
          :key="index"
        >
          <div class="title-wrapper">
            <p class="title">{{ keyPoint.title }}</p>
            <CustomTooltip
              :message="keyPoint.explanation"
              position="top"
              :allow-multi-line="true"
            >
              <AppIcon class-name="help-icon" name="info" />
            </CustomTooltip>
          </div>
          <p class="description">{{ keyPoint.description }}</p>
          <ReferenceDots
            :referencedParagraphs="keyPoint.references"
            :onResetReview="onResetKeypoints"
            :render-id="keyPoint.id!"
          />
        </div>
        <p v-if="conclusion" class="conclusion">{{ conclusion }}</p>
      </div>
      <div class="key-points-column" v-else>
        <p v-if="documentName" class="doc-name">{{ documentName }}</p>
        <div class="generation-error">
          <p class="error-message">{{ $t('reviewing.key_points.error') }}</p>
          <AppIcon
            class-name="re-generate-icon"
            name="counter-clock"
            @click="loadKeypoints"
          />
        </div>
      </div>
    </FloatingCard>
    <FloatingCard v-else>
      <BotMessageHeader :title="$t('reviewing.key_points.tab_title')" />
      <AnimatedDots />
    </FloatingCard>
  </div>
</template>

<script setup lang="ts">
import AnimatedDots from '@/components/common/animated-dots/AnimatedDots.vue';
import AppIcon from '@/components/common/app-icon/AppIcon.vue';
import BotMessageHeader from '@/components/common/bot-message-header/BotMessageHeader.vue';
import FloatingCard from '@/components/common/floating-card/FloatingCard.vue';
import CustomTooltip from '@/components/common/tooltip/CustomTooltip.vue';
import ReferenceDots from '@/components/Reviewing/reference-dots/ReferenceDots.vue';
import { KeyPoint } from '@/models/doc-review';
import documentReviewingService from '@/services/DocumentReviewingService';
import { useReviewingStore } from '@/stores/reviewingStore';
import { useReferenceSelectionStore } from '@/stores/useReferenceSelectionStore';
import { useToasterStore } from '@/stores/useToasterStore';
import { GlobalErrorHandler } from '@/utils/global-error.utils';
import { OfficeUtils } from '@/utils/office.utils';
import { v4 as uuidv4 } from 'uuid';
import { ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useKeyPointsExport } from '@/composables/useKeyPointsExport';
import IconButton from '@/components/common/button/IconButton.vue';

const loading = ref(false);
const reviewingStore = useReviewingStore();
const keyPoints = ref<KeyPoint[]>([]);
const conclusion = ref<string | undefined>();
const documentName = ref<string | undefined>();
const toaster = useToasterStore();
const referenceStore = useReferenceSelectionStore();
const { t } = useI18n();
const { onCopyKeypoints, onInsertKeypoints } = useKeyPointsExport();

const loadKeypoints = async () => {
  loading.value = true;
  try {
    if (reviewingStore.keyPointsResponse.keyPoints.length) {
      keyPoints.value = reviewingStore.keyPointsResponse.keyPoints;
      conclusion.value = reviewingStore.keyPointsResponse.conclusion;
    } else {
      const paragraphs = await OfficeUtils.getDocumentParagraphs();
      const apiResponse =
        await documentReviewingService.getDocumentKeypoints(paragraphs);
      keyPoints.value = apiResponse.keyPoints;

      conclusion.value = apiResponse.conclusion;
      reviewingStore.setKeypointsResponse(apiResponse);
    }
  } catch (error) {
    console.error(error);
    await GlobalErrorHandler.handleError(error, toaster, t);
  } finally {
    if (keyPoints.value.length) {
      keyPoints.value.forEach((keyPoint) => {
        keyPoint.id = uuidv4();
      });
    }
    loading.value = false;
  }
};

loadKeypoints();
documentName.value = OfficeUtils.getDocumentName();

const onResetKeypoints = () => {
  reviewingStore.setKeypointsResponse({ keyPoints: [], conclusion: '' });
  loadKeypoints();
};
</script>

<style scoped lang="scss">
.key-points-wrapper {
  flex-grow: 1;
  overflow: auto;
}

.key-points-column {
  display: flex;
  flex-direction: column;
  gap: $space-2;

  .doc-name {
    font-weight: $fw-medium;
    color: $color-text-dark;
    width: 100%;
    text-align: left;
    font-size: $body-m-font-size;
    line-height: $body-m-line-height;
  }

  .generation-error {
    display: inline-flex;
    align-items: center;
    gap: $space-1;

    .error-message {
      font-size: $body-s-font-size;
      line-height: $body-s-line-height;
      color: $color-error-500;
      font-weight: $fw-medium;
      flex: 1 1 auto;
    }

    .re-generate-icon {
      flex-shrink: 0;
      cursor: pointer;
      transition: 0.2s ease;

      &:hover {
        box-shadow: $elevation-1;
      }
    }
  }

  .conclusion {
    font-size: $body-s-font-size;
    line-height: $body-s-line-height;
  }
}

.key-point {
  display: flex;
  flex-direction: column;
  gap: $space-1;
  padding: $space-2;
  border-radius: $radius-2;
  border: 1px solid $color-neutral-200;

  *,
  * > * {
    user-select: none;
  }

  &.active {
    transition: 0.2s ease;

    .title,
    .description {
      margin-left: -2px;
    }

    border-left: 3px solid $color-primary-500;
  }

  &:hover {
    box-shadow: $elevation-2;
    transition: 0.2s ease;
  }

  .title-wrapper {
    display: inline-flex;
    align-items: center;
    gap: $space-1;

    .title {
      font-weight: $fw-medium;
      color: $color-text-darker;
      font-size: $body-s-font-size;
      line-height: $body-s-line-height;

      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
      flex: 1 1 auto;
    }

    .help-icon {
      flex-shrink: 0;
    }
  }

  .description {
    font-size: $body-s-font-size;
    line-height: $body-s-line-height;
    color: $color-text-dark;
    font-weight: $fw-regular;
  }
}

// Styles for big screens
@media (min-height: $small-screen-height) {
  .key-points-column {
    gap: $space-3;

    .doc-name {
      font-size: $body-l-font-size;
      line-height: $body-l-line-height;
    }

    .generation-error {
      .error-message {
        font-size: $body-m-font-size;
        line-height: $body-m-line-height;
      }
    }

    .conclusion {
      font-size: $body-m-font-size;
      line-height: $body-m-line-height;
    }
  }

  .key-point {
    padding: $space-3;

    .title {
      font-size: $body-m-font-size;
      line-height: $body-m-line-height;
    }

    .description {
      font-size: $body-m-font-size;
      line-height: $body-m-line-height;
    }
  }
}
</style>
